import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useEnhancedSecurity } from '@/hooks/useEnhancedSecurity';
import { useNotifications } from '@/hooks/useNotifications';
import { supabase } from '@/integrations/supabase/client';
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Clock, Shield } from 'lucide-react';

export const SessionTimeoutHandler: React.FC = () => {
  const { user } = useAuth();
  const { validateSessionSecurity } = useEnhancedSecurity();
  const { warning } = useNotifications();
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (!user) return;

    let activityTimer: NodeJS.Timeout;
    let warningTimer: NodeJS.Timeout;
    let countdownTimer: NodeJS.Timeout;

    const SESSION_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
    const WARNING_TIME = 5 * 60 * 1000; // Show warning 5 minutes before timeout

    const resetActivityTimer = () => {
      clearTimeout(activityTimer);
      clearTimeout(warningTimer);
      setShowTimeoutWarning(false);

      // Set warning timer
      warningTimer = setTimeout(() => {
        setShowTimeoutWarning(true);
        setTimeLeft(WARNING_TIME);
        
        // Start countdown
        countdownTimer = setInterval(() => {
          setTimeLeft(prev => {
            if (prev <= 1000) {
              handleSessionTimeout();
              return 0;
            }
            return prev - 1000;
          });
        }, 1000);
        
      }, SESSION_TIMEOUT - WARNING_TIME);

      // Set session timeout
      activityTimer = setTimeout(() => {
        handleSessionTimeout();
      }, SESSION_TIMEOUT);
    };

    const handleSessionTimeout = async () => {
      warning({
        title: 'Session expirée',
        description: 'Votre session a expiré par sécurité. Veuillez vous reconnecter.'
      });
      
      await supabase.auth.signOut();
      setShowTimeoutWarning(false);
    };

    const handleActivity = () => {
      // Ne valide pas la session à chaque événement pour éviter les appels excessifs
      // La validation périodique est déjà gérée par useEnhancedSecurity
      resetActivityTimer();
    };

    // Listen for user activity (sans mousemove pour éviter trop d'événements)
    const events = ['mousedown', 'keypress', 'click'];
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Initialize timer
    resetActivityTimer();

    return () => {
      clearTimeout(activityTimer);
      clearTimeout(warningTimer);
      clearInterval(countdownTimer);
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [user, validateSessionSecurity, warning]);

  const extendSession = () => {
    setShowTimeoutWarning(false);
    setTimeLeft(0);
    validateSessionSecurity();
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setShowTimeoutWarning(false);
  };

  const formatTime = (milliseconds: number): string => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <AlertDialog open={showTimeoutWarning} onOpenChange={setShowTimeoutWarning}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-warning" />
            Session sur le point d'expirer
          </AlertDialogTitle>
          <AlertDialogDescription>
            Votre session va expirer dans <strong>{formatTime(timeLeft)}</strong> par mesure de sécurité.
            Souhaitez-vous prolonger votre session ?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleLogout}>
            Se déconnecter
          </Button>
          <AlertDialogAction onClick={extendSession} className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Prolonger la session
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};